import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createUnitApi,
  deleteUnitApi,
  getUnitApi,
  getUnitsApi,
  updateUnitApi,
} from "../api/unitsApi";
import {
  catalogQueryKey,
  type CatalogScope,
} from "../api/catalogScope";
import type { UnitId } from "@KassaroAB/shared/types/ids";
import type { RestCreateUnit, RestUpdateUnit } from "@KassaroAB/shared/types/unit";

const unitsQueryKey = (scope: CatalogScope | null) =>
  catalogQueryKey(scope, "units");

const invalidateUnitQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  scope: CatalogScope | null,
) => {
  queryClient.invalidateQueries({ queryKey: unitsQueryKey(scope) });
};

export const useGetUnitsQuery = (scope: CatalogScope | null) => {
  return useQuery({
    queryKey: unitsQueryKey(scope),
    queryFn: () => getUnitsApi(scope!),
    enabled: !!scope,
  });
};

export const useGetUnitQuery = (
  scope: CatalogScope | null,
  unitId: UnitId | null | undefined,
) => {
  return useQuery({
    queryKey: [...unitsQueryKey(scope), unitId],
    queryFn: () => getUnitApi(scope!, unitId!),
    enabled: !!scope && !!unitId,
  });
};

export const useCreateUnitMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (unit: RestCreateUnit) => createUnitApi(scope!, unit),
    onSuccess: () => {
      invalidateUnitQueries(queryClient, scope);
    },
  });
};

export const useUpdateUnitMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      unitId,
      unit,
    }: {
      unitId: UnitId;
      unit: RestUpdateUnit;
    }) => updateUnitApi(scope!, unitId, unit),
    onSuccess: () => {
      invalidateUnitQueries(queryClient, scope);
    },
  });
};

export const useDeleteUnitMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (unitId: UnitId) => deleteUnitApi(scope!, unitId),
    onSuccess: () => {
      invalidateUnitQueries(queryClient, scope);
    },
  });
};
